<template>
  <div
    v-if="isOpen"
    class="modal-overlay-admin"
    @click="closeModal"
  >
    <div
      class="modal-admin w-full max-w-2xl"
      @click.stop
    >
      <div class="max-h-[90vh] min-h-[250px] flex flex-col">
        <!-- Header -->
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t('admin.modals.transaction_detail_title') }}
          </h3>
          <button
            class="modal-btn-admin-x"
            @click="closeModal"
          >
            <i class="fas fa-times" />
          </button>
        </div>

        <!-- Content -->
        <div
          v-if="transaction"
          class="modal-body scrollbar-glass"
        >
          <!-- Transaction Header -->
          <div class="transaction-header shadow-card">
            <div class="transaction-id">
              <span class="id-label">{{ t('admin.transactions.transaction_id') }}</span>
              <span class="id-value">{{ transaction.id }}</span>
            </div>
            <div class="transaction-status">
              <span
                class="status-badge"
                :class="`status-${transaction.status.toLowerCase()}`"
              >
                {{ getStatusText(transaction.status) }}
              </span>
            </div>
          </div>

          <!-- Transaction Amount -->
          <div class="amount-section shadow-card">
            <div class="amount-display">
              <span class="amount-value">{{ formatPrice(transaction.amount) }}</span>
              <span class="amount-currency">{{ transaction.currency }}</span>
            </div>
            <div class="transaction-type">
              <span
                class="type-badge"
                :class="`type-${transaction.type.toLowerCase()}`"
              >
                {{ getTypeText(transaction.type) }}
              </span>
            </div>
          </div>

          <!-- Transaction Details -->
          <div class="info-section">
            <h4 class="section-title">
              {{ t('admin.modals.transaction_info') }}
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.type') }}</label>
                <span class="info-value">{{ getTypeText(transaction.type) }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.amount') }}</label>
                <span class="info-value">{{ formatPrice(transaction.amount) }} {{ transaction.currency }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.events.status') }}</label>
                <span
                  class="status-badge"
                  :class="`status-${transaction.status.toLowerCase()}`"
                >
                  {{ getStatusText(transaction.status) }}
                </span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.payment_method') }}</label>
                <span class="info-value">{{ transaction.paymentMethod ? getPaymentMethodText(transaction.paymentMethod) : t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.reference_code') }}</label>
                <span class="info-value">{{ transaction.paymentReference || t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.payment_id') }}</label>
                <span class="info-value">{{ transaction.paymentId || t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.blockchain_hash') }}</label>
                <span class="info-value blockchain-hash">{{ transaction.blockchainTxHash || t('common.not_available') }}</span>
              </div>
            </div>
          </div>

          <!-- User Information -->
          <div class="info-section">
            <h4 class="section-title">
              {{ t('admin.modals.user_info') }}
            </h4>
            <div class="user-card shadow-card">
              <div class="user-avatar">
                <img
                  v-if="transaction.user?.avatar"
                  :src="transaction.user.avatar"
                  :alt="transaction.user?.name"
                  class="avatar-image"
                >
                <div v-else class="avatar-letter">
                  {{ transaction.user?.name?.charAt(0) || transaction.user?.email?.charAt(0) || 'M' }}
                </div>
              </div>
              <div class="user-details">
                <h5 class="user-name">
                  {{ transaction.user?.name }}
                </h5>
                <p class="user-email">
                  {{ transaction.user?.email }}
                </p>
              </div>
            </div>
          </div>

          <!-- Event Information -->
          <div
            v-if="transaction.event"
            class="info-section"
          >
            <h4 class="section-title">
              {{ t('admin.modals.event_info') }}
            </h4>
            <div class="event-card-local shadow-card">
              <div class="event-image">
                <img
                  :src="transaction.event.imageUrl || '/images/placeholder.jpg'"
                  :alt="transaction.event.title"
                  class="event-img"
                >
              </div>
              <div class="event-details">
                <h5 class="event-title">
                  {{ transaction.event.title }}
                </h5>
                <p class="event-description">
                  {{ (transaction.event.title || transaction.event.name || '')?.substring(0, 100) }}...
                </p>
              </div>
            </div>
          </div>

          <!-- Payment Details -->
          <div class="info-section">
            <h4 class="section-title">
              {{ t('admin.modals.payment_details') }}
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.payment_method_short') }}</label>
                <span class="info-value">{{ transaction.paymentMethod ? getPaymentMethodText(transaction.paymentMethod) : t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.reference_code') }}</label>
                <span class="info-value">{{ transaction.paymentReference || t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.payment_id') }}</label>
                <span class="info-value">{{ transaction.paymentId || t('common.not_available') }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.transactions.blockchain_hash') }}</label>
                <span class="info-value blockchain-hash">{{ transaction.blockchainTxHash || t('common.not_available') }}</span>
              </div>
            </div>
          </div>

          <!-- Timestamps -->
          <div class="info-section">
            <h4 class="section-title">
              {{ t('admin.modals.timestamps') }}
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label class="info-label">{{ t('admin.events.created_at') }}</label>
                <span class="info-value">{{ formatDateTime(transaction.createdAt) }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">{{ t('admin.events.updated_at') }}</label>
                <span class="info-value">{{ formatDateTime(transaction.updatedAt) }}</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div
            v-if="transaction.status === 'PENDING'"
            class="info-section"
          >
            <h4 class="section-title">
              {{ t('admin.modals.actions') }}
            </h4>
            <div class="action-buttons">
              <button
                class="btn btn-success"
                @click="handleApprove"
              >
                <i class="fas fa-check mr-2" />
                {{ t('admin.modals.approve_transaction') }}
              </button>
              <button
                class="btn btn-danger"
                @click="handleReject"
              >
                <i class="fas fa-times mr-2" />
                {{ t('admin.modals.reject_transaction') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            class="btn btn-secondary"
            @click="closeModal"
          >
            {{ t('admin.common.close') }}
          </button>
          <button
            v-if="transaction"
            class="btn btn-admin-primary"
            @click="editTransaction"
          >
            <i class="fas fa-edit mr-2" />
            {{ t('admin.common.edit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import type { Transaction } from '@/services/api/admin'

interface Props {
  isOpen: boolean
  transaction: Transaction | null
}

interface Emits {
  (e: 'close'): void
  (e: 'edit', transaction: Transaction): void
  (e: 'approve', transactionId: string): void
  (e: 'reject', transactionId: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const closeModal = () => {
  emit('close')
}

const editTransaction = () => {
  if (props.transaction) {
    emit('edit', props.transaction)
  }
}

const handleApprove = () => {
  if (props.transaction) {
    emit('approve', props.transaction.id)
  }
}

const handleReject = () => {
  if (props.transaction) {
    emit('reject', props.transaction.id)
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'PENDING': return t('admin.transactions.status_pending')
    case 'COMPLETED': return t('admin.transactions.status_completed')
    case 'FAILED': return t('admin.transactions.status_failed')
    case 'CANCELLED': return t('admin.transactions.status_cancelled')
    case 'REFUNDED': return t('admin.transactions.status_refunded')
    default: return status
  }
}

const getTypeText = (type: string) => {
  switch (type) {
    case 'PURCHASE': return t('admin.transactions.type_purchase')
    case 'REFUND': return t('admin.transactions.type_refund')
    case 'TRANSFER': return t('admin.transactions.type_transfer')
    case 'WITHDRAWAL': return t('admin.transactions.type_withdrawal')
    case 'DEPOSIT': return t('admin.transactions.type_deposit')
    default: return type
  }
}

const getPaymentMethodText = (method: string) => {
  switch (method) {
    case 'VNPAY': return 'VNPay'
    case 'BANK_TRANSFER': return t('admin.transactions.method_bank_transfer')
    case 'CRYPTO': return t('admin.transactions.method_crypto')
    case 'CASH': return t('admin.transactions.method_cash')
    default: return method || t('common.not_available')
  }
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('vi-VN').format(price)
}

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.modal-header {
  @apply flex-shrink-0 flex justify-between items-center gap-x-4 p-4 sm:p-6;
}

.modal-title {
  @apply text-xl font-semibold text-white;
}

.modal-body {
  @apply overflow-y-auto overscroll-contain flex-1 px-4 sm:px-6 py-4;
}

.modal-footer {
  @apply flex-shrink-0 flex justify-end gap-x-4 px-4 sm:px-6 py-4;
}

.transaction-header {
  @apply flex justify-between items-center mb-6 p-4 bg-white/5 border border-white/10 rounded-lg backdrop-blur-sm;
}

.transaction-id {
  @apply space-y-1;
}

.id-label {
  @apply block text-sm text-gray-300;
}

.id-value {
  @apply block text-lg font-mono text-white;
}

.transaction-status {
  @apply flex-shrink-0;
}

.amount-section {
  @apply text-center mb-6 p-6 bg-white/5 border border-white/10 rounded-lg backdrop-blur-sm;
}

.amount-display {
  @apply mb-2;
}

.amount-value {
  @apply text-3xl font-bold text-blue-500;
}

.amount-currency {
  @apply text-lg text-cyan-500 ml-2;
}

.transaction-type {
  @apply mt-2;
}

.info-section {
  @apply mb-6;
}

.section-title {
  @apply text-lg font-semibold text-white mb-4;
}

.info-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply block text-sm font-medium text-gray-400;
}

.info-value {
  @apply block text-sm text-white;
}

.blockchain-hash {
  @apply font-mono text-xs break-all;
}

.status-badge,
.type-badge {
  @apply inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium;
}

.status-pending {
  @apply bg-yellow-100 text-yellow-800;
}

.status-completed {
  @apply bg-green-100 text-green-800;
}

.status-failed {
  @apply bg-red-100 text-red-800;
}

.status-cancelled {
  @apply bg-gray-100 text-gray-800;
}

.status-refunded {
  @apply bg-blue-100 text-blue-800;
}

.type-purchase {
  @apply bg-green-100 text-green-800;
}

.type-refund {
  @apply bg-red-100 text-red-800;
}

.type-transfer {
  @apply bg-blue-100 text-blue-800;
}

.type-withdrawal {
  @apply bg-orange-100 text-orange-800;
}

.type-deposit {
  @apply bg-purple-100 text-purple-800;
}

.user-card,
.event-card-local {
  @apply flex items-center space-x-4 p-4 bg-white/5 border border-white/10 rounded-lg backdrop-blur-sm;
}

.user-avatar,
.event-image {
  @apply flex-shrink-0;
}

.avatar-image {
  @apply w-12 h-12 rounded-full object-cover;
}

.avatar-letter {
  @apply w-12 h-12 rounded-full bg-gray-700 text-xl flex justify-center items-center;
}

.event-img {
  @apply w-24 h-16 rounded-lg object-cover;
}

.user-details,
.event-details {
  @apply flex-1;
}

.user-name,
.event-title {
  @apply font-medium text-white;
}

.user-email,
.event-description {
  @apply text-sm text-gray-400;
}

.action-buttons {
  @apply flex gap-x-3;
}

.btn {
  @apply px-5 py-2.5;
}

.btn-success {
  @apply bg-green-600 text-white hover:bg-green-700;
}

.btn-danger {
  @apply bg-red-600 text-white hover:bg-red-700;
}
</style>
